import React, {useEffect, useState} from "react";
import {Modal, Spinner} from "react-bootstrap";
import {genericGetOne, apiGenericUpdate} from "../generic/crud";
import {ENTITY_ID_JOB_CARD} from "../generic/Constants";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../../utils/toast/Toast";
import {showConfirmDialog} from "../../generic-components/ConfirmDialog";
import {JobCardAddEditDialog} from "./JobCardAddEditDialog";
import {useAuth} from "../../modules/auth";

const formatDateWithOrdinal = (date) => {
    if (!date) return 'N/A';

    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    // Get ordinal suffix
    const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return `${getOrdinal(day)} ${month} ${year}, ${time}`;
};

const RunningTimer = ({startDate}) => {
    const [timeString, setTimeString] = useState('');

    useEffect(() => {
        if (!startDate) return;

        const update = () => {
            const now = new Date().getTime();
            const start = new Date(startDate).getTime();
            const diff = now - start;

            if (diff < 0) return;

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            let str = '';
            if (days > 0) str += `${days}d `;
            if (hours > 0) str += `${hours}h `;
            str += `${minutes}m ${seconds}s`;

            setTimeString(str);
        };

        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [startDate]);

    return <span className="font-monospace fw-bold ms-1">{timeString}</span>;
};

export function JobCardDetailsDialog({show, handleClose, jobCardId, onStatusChange}) {
    const {currentUser} = useAuth();
    const [jobCard, setJobCard] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    // Hide dealer price for SUPERVISOR_DASHBOARD and NO_DASHBOARD users
    const shouldShowDealerPrice = currentUser?.dashboardType !== 'SUPERVISOR_DASHBOARD' &&
                                   currentUser?.dashboardType !== 'NO_DASHBOARD';

    useEffect(() => {
        if (jobCardId && show) {
            fetchJobCardDetails();
        }
    }, [jobCardId, show]);

    const fetchJobCardDetails = async () => {
        setIsLoading(true);
        try {
            const {data} = await genericGetOne(ENTITY_ID_JOB_CARD, jobCardId);
            setJobCard(data);
        } catch (error) {
            showToast("Failed to load job card details", TOAST_FAILURE);
            handleClose();
        } finally {
            setIsLoading(false);
        }
    };

    const getNextStatus = (currentStatus) => {
        const statusFlow = {
            'OPEN': 'IN_PROGRESS',
            'IN_PROGRESS': 'CLOSED',
            'CLOSED': null
        };
        return statusFlow[currentStatus];
    };

    const getNextStatusLabel = (currentStatus) => {
        const labels = {
            'OPEN': 'Start Progress',
            'IN_PROGRESS': 'Mark as Done',
            'CLOSED': 'Completed'
        };
        return labels[currentStatus];
    };

    const handleStatusChange = async () => {
        if (!jobCard) return;

        const nextStatus = getNextStatus(jobCard.status);
        if (!nextStatus) return;

        // Check if trying to close and treatments are not all complete
        if (nextStatus === 'CLOSED') {
            const incompleteTreatments = jobCard.selectedTreatments?.filter(t => t.status !== 'COMPLETE') || [];
            if (incompleteTreatments.length > 0) {
                showToast(`Cannot close Job Card. ${incompleteTreatments.length} treatment(s) are not yet complete.`, TOAST_FAILURE);
                return;
            }
        }

        setIsUpdatingStatus(true);
        try {
            const updatedJobCard = {
                ...jobCard,
                status: nextStatus,
                // Set startDate when moving to IN_PROGRESS
                ...(nextStatus === 'IN_PROGRESS' && !jobCard.startDate ? {startDate: new Date()} : {}),
                // Set endDate when moving to CLOSED
                ...(nextStatus === 'CLOSED' && !jobCard.endDate ? {endDate: new Date()} : {})
            };

            const config = {suppressToast: true};
            await apiGenericUpdate(ENTITY_ID_JOB_CARD, updatedJobCard, jobCardId, config);

            showToast(`Job Card status updated to ${nextStatus.replace('_', ' ')}`, TOAST_SUCCESS);
            setJobCard(updatedJobCard);
            if (onStatusChange) onStatusChange();
        } catch (error) {
            showToast(error?.response?.data?.message || "Failed to update status", TOAST_FAILURE);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleTreatmentStatusChange = async (treatmentIndex, newStatus) => {
        if (!jobCard) return;

        const treatment = jobCard.selectedTreatments[treatmentIndex];
        const currentStatus = treatment.status || 'TODO';

        // Get confirmation message based on action
        let confirmMessage = '';
        if (newStatus === 'DELETED') {
            confirmMessage = `Are you sure you want to delete "${treatment.name}"? This action cannot be undone.`;
        } else if (newStatus === 'IN_PROGRESS') {
            confirmMessage = `Start working on "${treatment.name}"?`;
        } else if (newStatus === 'COMPLETE') {
            confirmMessage = `Mark "${treatment.name}" as complete?`;
        }

        // Show confirmation dialog
        const confirmed = await showConfirmDialog(confirmMessage);

        if (!confirmed) return;

        const updatedTreatments = [...jobCard.selectedTreatments];
        updatedTreatments[treatmentIndex] = {
            ...updatedTreatments[treatmentIndex],
            status: newStatus
        };

        // Check if job card should be auto-updated to IN_PROGRESS
        const shouldAutoStartJobCard = newStatus === 'IN_PROGRESS' && jobCard.status === 'OPEN';

        const updatedJobCard = {
            ...jobCard,
            selectedTreatments: updatedTreatments,
            // Auto-update to IN_PROGRESS if starting first treatment
            ...(shouldAutoStartJobCard ? {
                status: 'IN_PROGRESS',
                startDate: new Date()
            } : {})
        };

        try {
            const config = {suppressToast: true};
            await apiGenericUpdate(ENTITY_ID_JOB_CARD, updatedJobCard, jobCardId, config);

            // If status is DELETED, the backend will move it to deletedTreatments
            // So we need to refetch to get the updated state
            if (newStatus === 'DELETED') {
                const {data} = await genericGetOne(ENTITY_ID_JOB_CARD, jobCardId);
                setJobCard(data);
                showToast("Treatment deleted successfully", TOAST_SUCCESS);
            } else {
                setJobCard(updatedJobCard);
                if (shouldAutoStartJobCard) {
                    showToast(`Treatment started! Job Card moved to In Progress`, TOAST_SUCCESS);
                } else {
                    showToast(`Treatment status updated to ${newStatus.replace('_', ' ')}`, TOAST_SUCCESS);
                }
            }

            if (onStatusChange) onStatusChange();
        } catch (error) {
            showToast("Failed to update treatment status", TOAST_FAILURE);
        }
    };

    const StatusBadge = ({status}) => {
        const styles = {
            'OPEN': {color: 'primary', label: 'Open', icon: 'bi-hourglass'},
            'IN_PROGRESS': {color: 'warning', label: 'In Progress', icon: 'bi-gear-wide-connected'},
            'CLOSED': {color: 'success', label: 'Closed', icon: 'bi-check-circle-fill'}
        };
        const style = styles[status] || {color: 'secondary', label: status, icon: 'bi-circle'};

        return (
            <span className={`badge badge-light-${style.color} fs-6 fw-bold px-4 py-2`}>
                <i className={`bi ${style.icon} me-2`}></i>
                {style.label}
            </span>
        );
    };

    const JobTypeBadge = ({type}) => {
        const types = {
            'FREE_SERVICE': 'Free Service',
            'PAID_SERVICE': 'Paid Service',
            'RUNNING_REPAIR': 'Running Repair',
            'ACCIDENTAL_REPAIR': 'Accidental Repair'
        };
        return <span className="badge badge-light-info fs-7 px-3 py-2">{types[type] || type}</span>;
    };

    const TreatmentStatusBadge = ({status}) => {
        const styles = {
            'TODO': {color: 'secondary', label: 'To Do', icon: 'bi-circle'},
            'IN_PROGRESS': {color: 'warning', label: 'In Progress', icon: 'bi-arrow-repeat'},
            'COMPLETE': {color: 'success', label: 'Complete', icon: 'bi-check-circle-fill'}
        };
        const style = styles[status] || {color: 'secondary', label: status, icon: 'bi-circle'};

        return (
            <span className={`badge badge-light-${style.color} fs-8 fw-bold px-2 py-1`}>
                <i className={`bi ${style.icon} me-1`}></i>
                {style.label}
            </span>
        );
    };

    const getNextTreatmentStatus = (currentStatus) => {
        const statusFlow = {
            'TODO': 'IN_PROGRESS',
            'IN_PROGRESS': 'COMPLETE',
            'COMPLETE': null
        };
        return statusFlow[currentStatus || 'TODO'];
    };

    const getNextTreatmentStatusLabel = (currentStatus) => {
        const labels = {
            'TODO': 'Start',
            'IN_PROGRESS': 'Complete',
            'COMPLETE': 'Done'
        };
        return labels[currentStatus || 'TODO'];
    };

    if (!jobCard && !isLoading) return null;

    return (
        <>
            <Modal
                show={show && !showEdit}
                onHide={handleClose}
                centered
                fullscreen="sm-down"
            >
            <Modal.Header closeButton className="border-0 pb-2">
                <Modal.Title className="fs-5">Job Card Details</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2 pb-4" style={{overflowY: 'auto', maxHeight: '80vh'}}>
                {isLoading && (
                    <div className="text-center py-10">
                        <Spinner animation="border" variant="primary"/>
                        <p className="mt-3 text-gray-500">Loading job card details...</p>
                    </div>
                )}

                {!isLoading && jobCard && (
                    <div className="px-2">
                        {/* Header Section */}
                        <div className="d-flex flex-column align-items-center mb-8">
                            <div className="symbol symbol-60px symbol-circle bg-light-primary p-3 mb-4">
                                <i className="bi bi-car-front-fill fs-2x text-primary"></i>
                            </div>
                            <h2 className="fw-bolder text-gray-900 mb-1">{jobCard.vehicleNo}</h2>
                            <div className="d-flex align-items-center mt-2 gap-2">
                                <span className="badge badge-light-secondary text-gray-700 fs-7 fw-bold px-3 py-2">
                                    {jobCard.vehicleModelName || 'Model N/A'}
                                </span>
                                <StatusBadge status={jobCard.status}/>
                            </div>
                            {jobCard.status !== 'CLOSED' && (
                                <div className="text-gray-500 fs-7 mt-2">
                                    Created <RunningTimer startDate={jobCard.createdOn} /> ago
                                </div>
                            )}
                        </div>

                        {/* Action Button */}
                        {jobCard.status !== 'CLOSED' && (
                            <div className="mb-6">
                                <button
                                    className={`btn btn-${jobCard.status === 'OPEN' ? 'warning' : 'success'} w-100 py-3`}
                                    onClick={handleStatusChange}
                                    disabled={isUpdatingStatus}
                                    style={{minHeight: '48px'}}
                                >
                                    {isUpdatingStatus ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2"/>
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className={`bi ${jobCard.status === 'OPEN' ? 'bi-play-fill' : 'bi-check-circle-fill'} fs-4 me-2`}></i>
                                            <span className="fs-6 fw-bold">{getNextStatusLabel(jobCard.status)}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Job Information List */}
                        <div className="mb-8 bg-light-primary rounded p-4">
                            <h5 className="mb-4 fw-bold text-gray-800">Job Information</h5>
                            <div className="row g-4">
                                <div className="col-6">
                                    <label className="text-gray-500 fw-semibold fs-7 mb-1 d-block">RO Number</label>
                                    <span className="text-gray-900 fw-bold fs-6">{jobCard.roNumber}</span>
                                </div>
                                <div className="col-6">
                                    <label className="text-gray-500 fw-semibold fs-7 mb-1 d-block">Job Type</label>
                                    <div className="mt-1"><JobTypeBadge type={jobCard.jobType}/></div>
                                </div>
                                <div className="col-6">
                                    <label className="text-gray-500 fw-semibold fs-7 mb-1 d-block">Advisor</label>
                                    <span className="text-gray-900 fw-bold fs-6">{jobCard.advisorName || 'Not Assigned'}</span>
                                </div>
                                <div className="col-6">
                                    <label className="text-gray-500 fw-semibold fs-7 mb-1 d-block">Branch</label>
                                    <span className="text-gray-900 fw-bold fs-6">{jobCard.branchName || 'N/A'}</span>
                                </div>
                                <div className="col-6">
                                    <label className="text-gray-500 fw-semibold fs-7 mb-1 d-block">Created By</label>
                                    <span className="text-gray-900 fw-bold fs-6">{jobCard.createdByName || 'N/A'}</span>
                                </div>
                                <div className="col-6">
                                    <label className="text-gray-500 fw-semibold fs-7 mb-1 d-block">Created Date</label>
                                    <span className="text-gray-900 fw-bold fs-6">
                                        {formatDateWithOrdinal(jobCard.createdOn)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Treatments Section */}
                        <div className="separator separator-dashed my-6"></div>
                        
                        <div>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <h5 className="fw-bold text-gray-900 m-0">
                                    Treatments
                                    <span className="badge badge-circle badge-light-secondary ms-2 text-gray-600">
                                        {jobCard.selectedTreatments?.length || 0}
                                    </span>
                                </h5>
                                {jobCard.selectedTreatments?.length > 0 && (
                                    <div className="d-flex gap-3">
                                        <span className="badge badge-light-success fw-bold fs-7">
                                            Customer: ₹{jobCard.selectedTreatments.reduce((sum, t) => sum + (t.customerPrice || 0), 0).toFixed(2)}
                                        </span>
                                        {shouldShowDealerPrice && (
                                            <span className="badge badge-light-primary fw-bold fs-7">
                                                Dealer: ₹{jobCard.selectedTreatments.reduce((sum, t) => sum + (t.dealerPrice || 0), 0).toFixed(2)}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {!jobCard.selectedTreatments || jobCard.selectedTreatments.length === 0 ? (
                                <div className="text-center py-5 rounded bg-light border border-dashed border-gray-300">
                                    <i className="bi bi-info-circle fs-2 text-gray-400 mb-2 d-block"></i>
                                    <span className="text-muted fw-semibold">No treatments selected</span>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-3" style={{maxHeight: '500px', overflowY: 'auto'}}>
                                    {jobCard.selectedTreatments.map((treatment, index) => {
                                        const treatmentStatus = treatment.status || 'TODO';
                                        const nextStatus = getNextTreatmentStatus(treatmentStatus);

                                        return (
                                            <div key={index} className="card border border-gray-200 shadow-sm">
                                                <div className="card-body p-3 d-flex align-items-center justify-content-between">
                                                    {/* Left Side: Treatment Info */}
                                                    <div className="flex-grow-1 me-3" style={{minWidth: 0}}>
                                                        <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                                                            <span className="text-gray-900 fw-bold fs-6">{treatment.name}</span>
                                                            <span className={`badge badge-light-${treatment.category === 'HVT' ? 'primary' : 'info'} fw-bold fs-9 px-2 py-1`}>
                                                                {treatment.category === 'HVT' ? 'HVT' : 'LVT'}
                                                            </span>
                                                        </div>

                                                        <div className="d-flex align-items-center flex-wrap gap-3">
                                                            <TreatmentStatusBadge status={treatmentStatus} />
                                                            <div className="d-flex align-items-center gap-3 text-gray-600">
                                                                <div className="d-flex align-items-center fs-7">
                                                                    <span className="text-muted me-1">Cust:</span>
                                                                    <span className="text-success fw-bold">₹{treatment.customerPrice?.toFixed(2) || '0.00'}</span>
                                                                </div>
                                                                {shouldShowDealerPrice && (
                                                                    <div className="d-flex align-items-center fs-7">
                                                                        <span className="text-muted me-1">Dlr:</span>
                                                                        <span className="text-primary fw-bold">₹{treatment.dealerPrice?.toFixed(2) || '0.00'}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Right Side: Action Buttons */}
                                                    {jobCard.status !== 'CLOSED' && (
                                                        <div className="d-flex flex-column flex-sm-row align-items-center gap-2">
                                                            {nextStatus && (
                                                                <button
                                                                    className={`btn btn-icon btn-sm btn-light-${treatmentStatus === 'TODO' ? 'primary' : 'success'} rounded-circle`}
                                                                    onClick={() => handleTreatmentStatusChange(index, nextStatus)}
                                                                    title={getNextTreatmentStatusLabel(treatmentStatus)}
                                                                >
                                                                    <i className={`bi ${treatmentStatus === 'TODO' ? 'bi-play-fill' : 'bi-check'} fs-3`}></i>
                                                                </button>
                                                            )}
                                                            <button
                                                                className="btn btn-icon btn-sm btn-light-danger rounded-circle"
                                                                onClick={() => handleTreatmentStatusChange(index, 'DELETED')}
                                                                title="Delete treatment"
                                                            >
                                                                <i className="bi bi-trash fs-4"></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Deleted Treatments Section */}
                            {jobCard.deletedTreatments && jobCard.deletedTreatments.length > 0 && (
                                <div className="mt-6">
                                    <div className="d-flex align-items-center mb-3">
                                        <h5 className="fw-bold text-gray-600 m-0 fs-6">Deleted Treatments</h5>
                                        <span className="badge badge-circle badge-light-danger ms-2 text-danger">
                                            {jobCard.deletedTreatments.length}
                                        </span>
                                    </div>
                                    <div className="bg-light-danger rounded p-3">
                                        {jobCard.deletedTreatments.map((treatment, index) => (
                                            <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom border-danger-subtle last-border-0">
                                                <div className="d-flex flex-column">
                                                    <span className="text-gray-600 fw-bold fs-7 text-decoration-line-through">{treatment.name}</span>
                                                    <span className="text-muted fs-9">Deleted on {new Date().toLocaleDateString()}</span>
                                                </div>
                                                <span className="badge badge-light-danger fs-9">Deleted</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal.Body>
            {jobCard && (
                <Modal.Footer className="border-top">
                    <div className="d-flex w-100 gap-2">
                        {jobCard.status !== 'CLOSED' && (
                            <button
                                type="button"
                                className="btn btn-primary w-50 py-3"
                                onClick={() => setShowEdit(true)}
                                style={{minHeight: '48px'}}
                            >
                                <i className="bi bi-pencil-square fs-4 me-2"></i>
                                <span className="fs-6 fw-semibold">Edit</span>
                            </button>
                        )}
                        <button
                            type="button"
                            className={`btn btn-light ${jobCard.status === 'CLOSED' ? 'w-100' : 'w-50'} py-3`}
                            onClick={handleClose}
                            style={{minHeight: '48px'}}
                        >
                            <span className="fs-6 fw-semibold">Close</span>
                        </button>
                    </div>
                </Modal.Footer>
            )}
        </Modal>
            {showEdit && (
                <JobCardAddEditDialog
                    show={showEdit}
                    handleClose={() => setShowEdit(false)}
                    id={jobCardId}
                    onSave={() => {
                        setShowEdit(false);
                        fetchJobCardDetails();
                        if (onStatusChange) onStatusChange();
                    }}
                />
            )}
        </>
    );
}
